'use client';

import { Language, Subsidiary } from '@coolblue-development/constants';
import {
  Button,
  Chip,
  Divider,
  Flex,
  freddyTheme,
  Modal,
  ModalBody,
  ModalFooter,
  Typography
} from '@coolblue-development/freddy';
import showRelocationTagTrigger from '@coolblue-development/ga4-events/triggers/VacancyBanner/showRelocationTagTrigger';
import closeRelocationOverlayTrigger from '@coolblue-development/ga4-events/triggers/VacancyRelocationOverlay/closeRelocationOverlayTrigger';
import openRelocationOverlayTrigger from '@coolblue-development/ga4-events/triggers/VacancyRelocationOverlay/openRelocationOverlayTrigger';
import selectAboutRelocationButtonTrigger from '@coolblue-development/ga4-events/triggers/VacancyRelocationOverlay/selectAboutRelocationButtonTrigger';
import { Checkmark, HomeOutlined } from '@coolblue-development/icons';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useTranslation } from '@i18n/useTranslation';
import { routes } from '@utils/url-generators/data/legacyRoutes';
import { useState } from 'react';

export const Relocation = () => {
  const { t } = useTranslation('vacancyDetail.header.relocation');
  const modalTranslations = useFreddyTranslations('modal');

  const { trackEvent } = useAnalytics();

  const [isOpen, setIsOpen] = useState(false);
  const uspKeys = ['visaAndPermit', 'housing', 'languageCourse'];

  const relocationUrl = routes['relocation']?.[Subsidiary.NL]?.[Language.ENGLISH] ?? '#';

  return (
    <>
      <TrackImmediate trigger={showRelocationTagTrigger()} />
      <Chip
        variant='button'
        onClick={() => {
          setIsOpen(true);
          trackEvent(openRelocationOverlayTrigger());
        }}
        sx={{ boxShadow: freddyTheme.shadow.subtle }}
        startIcon={<HomeOutlined />}
      >
        {t('relocationChipText')}
      </Chip>

      <Modal
        translations={modalTranslations}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          trackEvent(
            closeRelocationOverlayTrigger({
              sub_feature: 'button'
            })
          );
        }}
        title={t('modal.title')}
        placement={{
          xs: 'bottom',
          md: 'center'
        }}
        size='medium'
        body={
          <ModalBody>
            <Flex
              sx={{ margin: `${freddyTheme.spacing[300]} 0` }}
              gap={freddyTheme.spacing[200]}
              direction={{ xs: 'column', md: 'row' }}
            >
              {uspKeys.map(uspKey => (
                <Flex key={uspKey} gap={freddyTheme.spacing[200]} alignItems='center'>
                  <Checkmark color={freddyTheme.color.border.sentimentPositive} />
                  <Typography variant='body'>{t(`modal.usps.${uspKey}`)}</Typography>
                </Flex>
              ))}
            </Flex>
            <Divider sx={{ marginBottom: freddyTheme.spacing[300] }} />
            <Typography variant='body'>{t('modal.description')}</Typography>
          </ModalBody>
        }
        footer={
          <ModalFooter>
            <Button
              component='a'
              onClick={() =>
                trackEvent(
                  selectAboutRelocationButtonTrigger({
                    index: 1,
                    total_indices: 1,
                    url: relocationUrl
                  })
                )
              }
              href={relocationUrl}
              style={{ width: '100%' }}
              target='_blank'
              rel='noopener noreferrer'
            >
              {t('modal.goToRelocationPage')}
            </Button>
          </ModalFooter>
        }
      />
    </>
  );
};
